<template>
  <router-view v-slot="{ Component }">
    <component :is="Component" v-model="company" @[reloadEvent(Component)]="reload" />
  </router-view>
</template>

<script lang="ts" setup>
import {
  computed, markRaw, provide, ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import GlobalLicenseFilterPicker from '@utils/globalLicenseFilter/GlobalLicenseFilterPicker.vue';
import globalLicenseFilterKey, { type GlobalLicenseFilter } from '@utils/globalLicenseFilter/injects';
import { useFetchQuery } from '@/hooks/fetch';
import { syncHeader } from '@/components/structure/navigation/injects';
import type { Company } from '@/entities/Entities';
import { syncPageBusy } from '@/components/utils/busy/injects';
import { useQueryRef } from '@/hooks/url';

const route = useRoute();
const {
  data: company,
  loading,
  refetch,
} = useFetchQuery<Company>(`/companies/${route.params.companyId}`);

syncPageBusy(loading);

const title = computed(() => company.value?.name ?? '');

const reloadEvent = (component: any) => (component?.type?.emits?.includes('reload') ? 'reload' : '');

const globalFilterValue = ref<GlobalLicenseFilter>({ boxType: null, licenseId: null });
provide(globalLicenseFilterKey, globalFilterValue);

const boxType = useQueryRef<string>('boxType', '');
const licenseId = useQueryRef<string>('license', '');
if (boxType.value !== '') globalFilterValue.value.boxType = boxType.value;
if (licenseId.value !== '') globalFilterValue.value.licenseId = licenseId.value;
watch(globalFilterValue, (newValue) => {
  if (newValue.boxType !== '') boxType.value = newValue.boxType ?? '';
  if (newValue.licenseId !== '') licenseId.value = newValue.licenseId ?? '';
}, { deep: true });

syncHeader(company, () => ({
  extraBreadcrumbs: [
    computed(() => ({
      label: title.value,
      link: {
        name: 'company-license-details',
        params: { companyId: company?.value?.id ?? '-' },
      },
    })),
  ],
  title: computed(() => ({
    text: title.value,
    link: {
      name: 'licenses',
    },
  })),
  globalFilterComponent: {
    component: markRaw(GlobalLicenseFilterPicker),
    props: {
      modelValue: globalFilterValue,
    },
    events: {
      'update:model-value': (e: GlobalLicenseFilter) => { globalFilterValue.value = e; },
    },
  },
}));

const reload = async () => {
  await refetch();
};
</script>

<style lang="scss" scoped></style>
